import { useState, useEffect, Fragment, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Download,
  ChevronRight,
  ChevronDown,
  Search,
  X,
  RotateCcw,
  SlidersHorizontal,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUpDown,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const fmtINR = (v) => {
  if (v == null || v === "") return "0";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  let val;
  if (abs >= 1_00_00_000) val = `${sign}${(abs / 1_00_00_000).toFixed(2)} Cr`;
  else if (abs >= 1_00_000) val = `${sign}${(abs / 1_00_000).toFixed(2)} L`;
  else val = `${sign}${abs.toLocaleString("en-IN")}`;
  return val;
};

const fmtExact = (value, currency) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  const exact = number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${exact} ${currency}` : exact;
};

const AmountText = ({ value, currency, colored = false }) => {
  const num = Number(value || 0);
  const isPositive = num > 0;
  const isNegative = num < 0;
  return (
    <span
      title={fmtExact(value, currency)}
      className={
        colored
          ? isPositive
            ? "text-emerald-600 dark:text-emerald-400 font-semibold"
            : isNegative
              ? "text-rose-600 dark:text-rose-400 font-semibold"
              : ""
          : ""
      }
    >
      {fmtINR(value)}
    </span>
  );
};

const VarianceBadge = ({ diff, relDiff }) => {
  const num = Number(diff || 0);
  const rel = Number(relDiff || 0);
  if (!num && !rel) return <span className="text-muted-foreground text-xs">0.0%</span>;

  const isPos = num > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
        isPos
          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
          : "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
      }`}
    >
      {isPos ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {isPos ? "+" : ""}
      {rel.toFixed(1)}%
    </span>
  );
};

const now = new Date();
const defaultFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;

// SAP fiscal periods (K4): 1=Apr, 2=May, ..., 9=Dec, 10=Jan, 11=Feb, 12=Mar
const PERIODS = Array.from({ length: 12 }, (_, i) => {
  const p = i + 1;
  const month = ((p - 1 + 3) % 12) + 1;
  const monthName = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][month - 1];
  return { value: String(p), label: `${String(p).padStart(2, "0")} - ${monthName}` };
});

const getMonthDateRange = (fy, pNum) => {
  const p = Number(pNum) || 1;
  const year = p <= 9 ? Number(fy) : Number(fy) + 1;
  const month = ((p - 1 + 3) % 12) + 1;
  const lastDay = new Date(year, month, 0).getDate();
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const to = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
};

const DEFAULT_FSVS = [
  { version: "1000", description: "Kimberlite india Financial Stmt Version", chart_of_accounts: "YCOA" },
  { version: "1810", description: "SAP Best Practices Financial Stmt Version", chart_of_accounts: "YCOA" },
  { version: "FPA1", description: "SAP Best Practices Financial Stmt for FP&A", chart_of_accounts: "YCOA" },
  { version: "YPS2", description: "SAP Best Practices Contribution Margin", chart_of_accounts: "YCOA" },
];

const TrialBalance = ({
  data,
  loading,
  viewMode = "comparison",
  companyCode,
  fiscalYear,
  statementVersion = "1000",
  statementVersions = [],
  ledger = "0L",
  fromDate = "",
  toDate = "",
  periodNumber = "",
  comparisonPeriodNumber = "",
  comparisonYear = "",
  profitCenter = "",
  segment = "",
  onReset,
}) => {
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const fsvList = statementVersions.length > 0 ? statementVersions : DEFAULT_FSVS;

  // Auto-expand top level sections when data changes
  useEffect(() => {
    if (data?.sections) {
      const initial = new Set();
      data.sections.forEach((s) => initial.add(`sec-${s.id}`));
      setExpanded(initial);
    }
  }, [data]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set();
    (data?.sections || []).forEach((sec) => {
      all.add(`sec-${sec.id}`);
      (sec.subcategories || []).forEach((sub) => {
        all.add(`sub-${sub.id}`);
        (sub.line_items || []).forEach((li) => all.add(`li-${sub.id}-${li.id}`));
      });
    });
    setExpanded(all);
  };

  const collapseAll = () => setExpanded(new Set());

  const handleLocalReset = () => {
    setSearch("");
    setSortBy("default");
    if (onReset) onReset();
  };

  // GL search filter and sorting
  const { filteredSections, matchedGLs, forcedExpanded, matchCount } = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!data?.sections) {
      return {
        filteredSections: [],
        matchedGLs: new Set(),
        forcedExpanded: null,
        matchCount: 0,
      };
    }

    const sortGLList = (gls) => {
      if (!gls || sortBy === "default") return gls;
      const sorted = [...gls];
      if (sortBy === "code_asc") sorted.sort((a, b) => (a.gl_account || "").localeCompare(b.gl_account || ""));
      if (sortBy === "code_desc") sorted.sort((a, b) => (b.gl_account || "").localeCompare(a.gl_account || ""));
      if (sortBy === "name_asc") sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      if (sortBy === "name_desc") sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
      if (sortBy === "balance_desc") {
        sorted.sort((a, b) => Math.abs(Number(b.closing_balance ?? b.period_balance ?? 0)) - Math.abs(Number(a.closing_balance ?? a.period_balance ?? 0)));
      }
      if (sortBy === "balance_asc") {
        sorted.sort((a, b) => Math.abs(Number(a.closing_balance ?? a.period_balance ?? 0)) - Math.abs(Number(b.closing_balance ?? b.period_balance ?? 0)));
      }
      return sorted;
    };

    if (!q) {
      // No search query: apply sorting to all line items' GL accounts
      if (sortBy === "default") {
        return {
          filteredSections: data.sections,
          matchedGLs: new Set(),
          forcedExpanded: null,
          matchCount: 0,
        };
      }
      const sortedSections = data.sections.map((sec) => ({
        ...sec,
        subcategories: (sec.subcategories || []).map((sub) => ({
          ...sub,
          line_items: (sub.line_items || []).map((li) => ({
            ...li,
            gl_accounts: sortGLList(li.gl_accounts || []),
          })),
        })),
      }));
      return {
        filteredSections: sortedSections,
        matchedGLs: new Set(),
        forcedExpanded: null,
        matchCount: 0,
      };
    }

    const matched = new Set();
    const forced = new Set();
    const filtered = [];
    for (const sec of data.sections) {
      const secSubs = [];
      for (const sub of sec.subcategories || []) {
        const subLis = [];
        for (const li of sub.line_items || []) {
          let gls = (li.gl_accounts || []).filter(
            (gl) =>
              gl.gl_account.toLowerCase().includes(q) ||
              (gl.name || "").toLowerCase().includes(q)
          );
          if (gls.length > 0) {
            gls.forEach((g) => matched.add(g.gl_account));
            forced.add(`sec-${sec.id}`);
            forced.add(`sub-${sub.id}`);
            forced.add(`li-${sub.id}-${li.id}`);
            gls = sortGLList(gls);
            subLis.push({
              ...li,
              opening_balance: gls.reduce((s, g) => s + Number(g.opening_balance || 0), 0),
              debit: gls.reduce((s, g) => s + Number(g.debit || 0), 0),
              credit: gls.reduce((s, g) => s + Number(g.credit || 0), 0),
              closing_balance: gls.reduce((s, g) => s + Number(g.closing_balance || 0), 0),
              period_balance: gls.reduce((s, g) => s + Number(g.period_balance || 0), 0),
              comparison_balance: gls.reduce((s, g) => s + Number(g.comparison_balance || 0), 0),
              absolute_difference: gls.reduce((s, g) => s + Number(g.absolute_difference || 0), 0),
              gl_accounts: gls,
            });
          }
        }
        if (subLis.length > 0) {
          secSubs.push({
            ...sub,
            total_opening: subLis.reduce((s, l) => s + Number(l.opening_balance || 0), 0),
            total_debit: subLis.reduce((s, l) => s + Number(l.debit || 0), 0),
            total_credit: subLis.reduce((s, l) => s + Number(l.credit || 0), 0),
            total_closing: subLis.reduce((s, l) => s + Number(l.closing_balance || 0), 0),
            total_comparison: subLis.reduce((s, l) => s + Number(l.comparison_balance || 0), 0),
            total_abs_diff: subLis.reduce((s, l) => s + Number(l.absolute_difference || 0), 0),
            line_items: subLis,
          });
        }
      }
      if (secSubs.length > 0) {
        filtered.push({
          ...sec,
          total_opening: secSubs.reduce((s, sb) => s + Number(sb.total_opening || 0), 0),
          total_debit: secSubs.reduce((s, sb) => s + Number(sb.total_debit || 0), 0),
          total_credit: secSubs.reduce((s, sb) => s + Number(sb.total_credit || 0), 0),
          total_closing: secSubs.reduce((s, sb) => s + Number(sb.total_closing || 0), 0),
          total_comparison: secSubs.reduce((s, sb) => s + Number(sb.total_comparison || 0), 0),
          total_abs_diff: secSubs.reduce((s, sb) => s + Number(sb.total_abs_diff || 0), 0),
          subcategories: secSubs,
        });
      }
    }
    return {
      filteredSections: filtered,
      matchedGLs: matched,
      forcedExpanded: forced,
      matchCount: matched.size,
    };
  }, [search, data, sortBy]);

  const isExpanded = (id) => (forcedExpanded ? forcedExpanded.has(id) : expanded.has(id));

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/financial-agent/export/trial-balance-report`, {
        params: {
          fiscal_year: fiscalYear,
          period_number: periodNumber,
          company_code: companyCode,
          ledger,
          from_date: fromDate || data?.from_date || undefined,
          to_date: toDate || data?.to_date || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `trial_balance_${fiscalYear}_${fromDate || "P" + String(periodNumber || "01")}_${toDate || ""}_${statementVersion}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Trial Balance exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const currentFSV = fsvList.find((v) => v.version === statementVersion);

  return (
    <div className="space-y-4" data-testid="trial-balance-page">
      {/* Search and Table Actions Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-card rounded-lg border shadow-sm">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="h-8.5 pl-8 pr-7 text-xs w-full"
              placeholder="Search GL accounts or descriptions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="tb-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                data-testid="tb-search-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-8.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
            onClick={handleLocalReset}
            data-testid="tb-reset-filters"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Sort Selector */}
          <div className="flex items-center gap-1">
            <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8.5 w-[165px] text-xs" data-testid="tb-sort-select">
                <SelectValue placeholder="Sort GL Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Hierarchy Default</SelectItem>
                <SelectItem value="code_asc">G/L Code (Ascending)</SelectItem>
                <SelectItem value="code_desc">G/L Code (Descending)</SelectItem>
                <SelectItem value="name_asc">Name (A → Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z → A)</SelectItem>
                <SelectItem value="balance_desc">Highest Balance First</SelectItem>
                <SelectItem value="balance_asc">Lowest Balance First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8.5 text-xs gap-1.5"
            onClick={expanded.size > 0 ? collapseAll : expandAll}
            data-testid="tb-expand-all"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {expanded.size > 0 ? "Collapse All" : "Expand All"}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="h-8.5 text-xs gap-1.5"
            onClick={handleExport}
            disabled={exporting || !data}
            data-testid="tb-export-btn"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export
          </Button>
        </div>
      </div>


      {/* Active FSV & Filter Info Banner */}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {currentFSV && (
              <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded font-medium">
                FSV: {currentFSV.version} ({currentFSV.description})
              </span>
            )}
            {profitCenter && (
              <span className="bg-muted px-2 py-0.5 rounded">
                Profit Center: <strong>{profitCenter}</strong>
              </span>
            )}
            {segment && (
              <span className="bg-muted px-2 py-0.5 rounded">
                Segment: <strong>{segment}</strong>
              </span>
            )}
          </div>
          <span className="flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            Reporting Currency: <strong>{data?.currency || "INR"}</strong>
          </span>
        </div>
      )}

      {search && data && matchCount > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2 px-1" data-testid="tb-search-status">
          <Search className="w-3.5 h-3.5" />
          Showing <span className="font-semibold text-foreground">{matchCount}</span> matching GL accounts - parents auto-expanded.
        </div>
      )}

      {/* Loading & Data Rendering */}
      {loading ? (
        <div className="flex items-center justify-center h-64 border rounded-lg bg-card">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">Loading SAP Trial Balance & Variances...</span>
        </div>
      ) : !data ? (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-card rounded-lg border shadow-sm my-2" data-testid="tb-empty-prompt">
          <Layers className="w-10 h-10 text-muted-foreground/50 mb-3" />
          <h3 className="text-base font-semibold">Selection Criteria Required</h3>
          <p className="text-sm text-muted-foreground max-w-md mt-1">
            Please select <strong>Company</strong>, <strong>Statement Version</strong>, <strong>Ledger</strong>, <strong>From Date</strong>, and <strong>To Date</strong> above, then click <strong>GO</strong> to generate the Trial Balance.
          </p>
        </div>
      ) : (

        <div className="border rounded-lg overflow-auto shadow-sm bg-card">
          <div className="border-b bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-center justify-between gap-4">
            <span>
              <strong>{data.company_name || `Company ${data.company_code}`}</strong> | Ledger {data.ledger} | Version:{" "}
              {data.statement_version}
              {data.from_date && data.to_date && (
                <span className="ml-2 font-mono text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                  {data.from_date} → {data.to_date}
                </span>
              )}
            </span>
            <span>
              {viewMode === "comparison" ? "S/4HANA Period Balance & Variance Mode" : "Opening / Debit / Credit / Closing Mode"}
            </span>
          </div>

          <Table>
            <TableHeader>
              {viewMode === "comparison" ? (
                <TableRow>
                  <TableHead className="min-w-[340px]">Hierarchy Node / Description</TableHead>
                  <TableHead className="min-w-[120px]">G/L Account</TableHead>
                  <TableHead className="text-right min-w-[130px]">
                    Period Balance {data.from_date && data.to_date ? `(${data.from_date} to ${data.to_date})` : `(${data.period || `P${periodNumber}`})`}
                  </TableHead>
                  <TableHead className="text-right min-w-[130px]">
                    Comparison Balance ({data.comparison_period_str || (comparisonPeriodNumber ? `P${comparisonPeriodNumber}` : "—")})
                  </TableHead>
                  <TableHead className="text-right min-w-[130px]">Absolute Difference</TableHead>
                  <TableHead className="text-right min-w-[110px]">Relative Difference</TableHead>
                </TableRow>
              ) : (
                <TableRow>
                  <TableHead className="min-w-[340px]">Hierarchy Node / Description</TableHead>
                  <TableHead className="min-w-[120px]">G/L Account</TableHead>
                  <TableHead className="text-right min-w-[130px]">Opening Balance</TableHead>
                  <TableHead className="text-right min-w-[130px]">Debit</TableHead>
                  <TableHead className="text-right min-w-[130px]">Credit (Signed)</TableHead>
                  <TableHead className="text-right min-w-[130px]">Closing Balance</TableHead>
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {(filteredSections || []).map((sec) => {
                const secId = `sec-${sec.id}`;
                const secOpen = isExpanded(secId);
                return (
                  <Fragment key={secId}>
                    <TableRow
                      className="bg-primary/10 hover:bg-primary/15 cursor-pointer font-medium"
                      onClick={() => !forcedExpanded && toggle(secId)}
                      data-testid={`tb-section-${sec.id}`}
                    >
                      <TableCell className="font-bold uppercase tracking-wider text-sm">
                        <span className="inline-flex items-center gap-1">
                          {secOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {sec.name}
                        </span>
                      </TableCell>

                      {viewMode === "comparison" ? (
                        <>
                          <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_closing} currency={data?.currency} />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_comparison} currency={data?.currency} />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_abs_diff} currency={data?.currency} colored />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <VarianceBadge diff={sec.total_abs_diff} relDiff={sec.total_rel_diff} />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_opening} currency={data?.currency} />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_debit} currency={data?.currency} />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_credit} currency={data?.currency} />
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums">
                            <AmountText value={sec.total_closing} currency={data?.currency} />
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {secOpen &&
                      (sec.subcategories || []).map((sub) => {
                        const subId = `sub-${sub.id}`;
                        const subOpen = isExpanded(subId);
                        return (
                          <Fragment key={subId}>
                            <TableRow
                              className="bg-muted/60 hover:bg-muted cursor-pointer"
                              onClick={() => !forcedExpanded && toggle(subId)}
                              data-testid={`tb-sub-${sub.id}`}
                            >
                              <TableCell className="pl-7 font-semibold">
                                <span className="inline-flex items-center gap-1">
                                  {subOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  {sub.name}
                                </span>
                              </TableCell>

                              {viewMode === "comparison" ? (
                                <>
                                  <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_closing} currency={data?.currency} />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_comparison} currency={data?.currency} />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_abs_diff} currency={data?.currency} colored />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <VarianceBadge diff={sub.total_abs_diff} relDiff={sub.total_rel_diff} />
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_opening} currency={data?.currency} />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_debit} currency={data?.currency} />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_credit} currency={data?.currency} />
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    <AmountText value={sub.total_closing} currency={data?.currency} />
                                  </TableCell>
                                </>
                              )}
                            </TableRow>

                            {subOpen &&
                              (sub.line_items || []).map((li) => {
                                const liId = `li-${sub.id}-${li.id}`;
                                const liOpen = isExpanded(liId);
                                return (
                                  <Fragment key={liId}>
                                    <TableRow
                                      className="hover:bg-muted/30 cursor-pointer"
                                      onClick={() => !forcedExpanded && toggle(liId)}
                                      data-testid={`tb-li-${li.id}`}
                                    >
                                      <TableCell className="pl-12">
                                        <span className="inline-flex items-center gap-1">
                                          {liOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                          {li.name}
                                        </span>
                                      </TableCell>

                                      {viewMode === "comparison" ? (
                                        <>
                                          <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.period_balance ?? li.closing_balance} currency={data?.currency} />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.comparison_balance} currency={data?.currency} />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.absolute_difference} currency={data?.currency} colored />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <VarianceBadge diff={li.absolute_difference} relDiff={li.relative_difference} />
                                          </TableCell>
                                        </>
                                      ) : (
                                        <>
                                          <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.opening_balance} currency={data?.currency} />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.debit} currency={data?.currency} />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.credit} currency={data?.currency} />
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums">
                                            <AmountText value={li.closing_balance} currency={data?.currency} />
                                          </TableCell>
                                        </>
                                      )}
                                    </TableRow>

                                    {liOpen &&
                                      (li.gl_accounts || []).map((gl) => (
                                        <TableRow
                                          key={`gl-${gl.gl_account}`}
                                          className={`text-xs ${
                                            matchedGLs.has(gl.gl_account)
                                              ? "bg-amber-50/70 dark:bg-amber-950/30"
                                              : "text-muted-foreground hover:bg-muted/20"
                                          }`}
                                          data-testid={`tb-gl-${gl.gl_account}`}
                                        >
                                          <TableCell className="pl-18 font-medium text-foreground">
                                            {gl.name}
                                          </TableCell>
                                          <TableCell className="font-mono text-foreground font-semibold">
                                            {gl.gl_account}
                                          </TableCell>

                                          {viewMode === "comparison" ? (
                                            <>
                                              <TableCell className="text-right tabular-nums text-foreground">
                                                <AmountText value={gl.period_balance ?? gl.closing_balance} currency={data?.currency} />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums">
                                                <AmountText value={gl.comparison_balance} currency={data?.currency} />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums">
                                                <AmountText value={gl.absolute_difference} currency={data?.currency} colored />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums">
                                                <VarianceBadge diff={gl.absolute_difference} relDiff={gl.relative_difference} />
                                              </TableCell>
                                            </>
                                          ) : (
                                            <>
                                              <TableCell className="text-right tabular-nums">
                                                <AmountText value={gl.opening_balance} currency={data?.currency} />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums">
                                                <AmountText value={gl.debit} currency={data?.currency} />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums">
                                                <AmountText value={gl.credit} currency={data?.currency} />
                                              </TableCell>
                                              <TableCell className="text-right tabular-nums text-foreground font-medium">
                                                <AmountText value={gl.closing_balance} currency={data?.currency} />
                                              </TableCell>
                                            </>
                                          )}
                                        </TableRow>
                                      ))}
                                  </Fragment>
                                );
                              })}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}

              {/* Grand Total */}
              {data.grand_totals && !search && (
                <TableRow className="bg-primary/15 font-bold border-t-2" data-testid="tb-grand-total">
                  <TableCell className="uppercase font-bold">Grand Total</TableCell>
                  {viewMode === "comparison" ? (
                    <>
                      <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.closing} currency={data?.currency} />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.comparison} currency={data?.currency} />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.absolute_difference} currency={data?.currency} colored />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <VarianceBadge
                          diff={data.grand_totals.absolute_difference}
                          relDiff={data.grand_totals.relative_difference}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-xs text-muted-foreground font-mono">—</TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.opening} currency={data?.currency} />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.debit} currency={data?.currency} />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.credit} currency={data?.currency} />
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums">
                        <AmountText value={data.grand_totals.closing} currency={data?.currency} />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default TrialBalance;

